import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { BookOpen, Calendar, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getTodayBibleReading } from '../services/api';
import TranslatedContent from './TranslatedContent';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const TodayReadingsWidget = ({ compact = false }) => {
  const { t, language } = useLanguage();
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadTodayReading = useCallback(async () => {
    try {
      const data = await getTodayBibleReading();
      setReading(data);
    } catch (error) {
      console.error('Error loading today\'s reading:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodayReading();
  }, [loadTodayReading]);

  if (loading) {
    return (
      <Card className="border-0 shadow-xl card-hover">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">{t('loading')}</span>
        </CardContent>
      </Card>
    );
  }

  if (!reading) {
    return (
      <Card className="border-0 shadow-xl card-hover bg-gradient-to-br from-blue-50 to-green-50">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <BookOpen className="w-6 h-6 text-blue-600" />
            <h3 className="font-serif font-bold text-xl text-gray-900">{t('todayReading')}</h3>
          </div>
          <p className="text-sm text-gray-600">
            <AutoTranslate>Inga läsningar tillgängliga för idag</AutoTranslate>
          </p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const localeMap = {
      sv: 'sv-SE',
      en: 'en-US',
      pl: 'pl-PL',
      es: 'es-ES',
      ar: 'ar-SA',
      fr: 'fr-FR',
    };
    const locale = localeMap[language] || 'sv-SE';
    return date.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (compact) {
    return (
      <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-shadow bg-white">
        <CardContent className="p-6">
          <div className="flex items-center space-x-3 mb-3">
            <BookOpen className="w-5 h-5 text-blue-600" />
            <h3 className="font-serif font-bold text-lg text-gray-900">{t('todayReading')}</h3>
          </div>
          <div className="flex items-center space-x-2 text-xs text-gray-500 mb-4">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(reading.date)}</span>
          </div>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <h4 className="text-xs font-semibold text-blue-900 mb-1">{t('firstReading')}</h4>
              <TranslatedContent
                content={reading.first_reading}
                as="p"
                className="text-sm text-gray-800 line-clamp-2"
              />
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <h4 className="text-xs font-semibold text-green-900 mb-1">{t('gospel')}</h4>
              <TranslatedContent
                content={reading.gospel}
                as="p"
                className="text-sm text-gray-800 line-clamp-2"
              />
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500 italic">
            <AutoTranslate>Texter ur Bibel 2000 © Svenska Bibelsällskapet</AutoTranslate>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-2xl bg-gradient-to-br from-blue-50 to-green-50">
      <CardContent className="p-8">
        <div className="flex items-center space-x-3 mb-4">
          <BookOpen className="w-8 h-8 text-blue-600" />
          <h2 className="text-3xl font-serif font-bold text-gray-900">{t('todayReading')}</h2>
        </div>
        
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-6">
          <Calendar className="w-4 h-4" />
          <span>{formatDate(reading.date)}</span>
        </div>

        <div className="space-y-6">
          {/* First Reading */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
              {t('firstReading')}
            </h3>
            <TranslatedContent
              content={reading.first_reading}
              as="p"
              className="text-gray-700 leading-relaxed"
            />
          </div>

          {/* Psalm */}
          <div className="bg-white rounded-lg p-6 shadow-md">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="w-2 h-2 bg-purple-600 rounded-full mr-2"></span>
              {t('psalm')}
            </h3>
            <TranslatedContent
              content={reading.psalm}
              as="p"
              className="text-gray-700 leading-relaxed italic"
            />
          </div>

          {/* Second Reading */}
          {reading.second_reading && (
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                {t('secondReading')}
              </h3>
              <TranslatedContent
                content={reading.second_reading}
                as="p"
                className="text-gray-700 leading-relaxed"
              />
            </div>
          )}

          {/* Gospel */}
          <div className="bg-white rounded-lg p-6 shadow-md border-2 border-yellow-200">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <span className="w-2 h-2 bg-yellow-600 rounded-full mr-2"></span>
              {t('gospel')}
            </h3>
            <TranslatedContent
              content={reading.gospel}
              as="p"
              className="text-gray-700 leading-relaxed font-medium"
            />
          </div>

          {/* Reflection */}
          {reading.reflection && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 shadow-md">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <span className="w-2 h-2 bg-orange-600 rounded-full mr-2"></span>
                {t('reflection')}
              </h3>
              <TranslatedContent
                content={reading.reflection}
                as="p"
                className="text-gray-700 leading-relaxed"
              />
            </div>
          )}
        </div>

        {/* Copyright Notice */}
        <div className="mt-6 pt-4 border-t border-gray-300">
          <p className="text-xs text-gray-500 text-center italic">
            <AutoTranslate>Texter ur Bibel 2000 © Svenska Bibelsällskapet</AutoTranslate>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TodayReadingsWidget;
